import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api, { refreshSession } from "@/lib/api";

const AuthContext = createContext(null);

// Access token lifetime is 10 min; renew a bit before expiry to keep the session
// alive silently while the app is open.
const SILENT_RENEW_MS = 8 * 60 * 1000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading, null = unauth
  const renewTimer = useRef(null);

  const checkAuth = useCallback(async () => {
    // Initial / reload probe. Uses skipAuthRefresh so the interceptor does NOT
    // fire a background refresh+logout (which could race a login). We do the
    // refresh ourselves, sequentially, to survive reloads with a valid refresh.
    try {
      const { data } = await api.get("/auth/me", { skipAuthRefresh: true });
      setUser(data);
    } catch {
      try {
        await refreshSession();
        const { data } = await api.get("/auth/me", { skipAuthRefresh: true });
        setUser(data);
      } catch {
        setUser(null);
      }
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Silent renewal: while authenticated, proactively rotate before the access
  // token expires. Failures fall through to the api interceptor's logout event.
  useEffect(() => {
    if (renewTimer.current) clearInterval(renewTimer.current);
    if (!user) return;
    renewTimer.current = setInterval(() => {
      refreshSession().catch(() => window.dispatchEvent(new Event("auth:logout")));
    }, SILENT_RENEW_MS);
    return () => renewTimer.current && clearInterval(renewTimer.current);
  }, [user]);

  // The api interceptor dispatches this when a refresh ultimately fails
  // (refresh token expired / revoked / reuse-detected) → automatic logout.
  useEffect(() => {
    const onLogout = () => {
      localStorage.removeItem("growthai_org");
      setUser(null);
    };
    window.addEventListener("auth:logout", onLogout);
    return () => window.removeEventListener("auth:logout", onLogout);
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    localStorage.removeItem("growthai_org");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, checkAuth, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
